import React from 'react';
import { useMemo } from 'react';
import { DatePickerMobileSelectorColumnProps } from './props';
import styles from './styles.module.scss';
import DatePickerMobileSelectorColumnCell from './Cell';
import { useTranslation } from '../../../../../hooks';
import { dateFormatMapping } from '../../../../../constants/date/date-format-mapping';
import { Language } from '../../../../../enums/language';
import { useScrollEffect } from '../../../../../hooks/date-picker/useScrollEffectHandler';

/**
 * Component that renders a scrollable column for date picker selection on mobile
 * Handles scroll interactions and displays date values in a vertical list
 *
 * @component
 * @template T - Generic type for column content
 * @param {Object} props - Component props
 * @param {Array} props.content - Array of values to display in the column
 * @param {Function} props.onSelectedItem - Callback when an item is selected
 * @param {number} props.currentIndex - Currently selected index
 * @param {'day'|'month'|'year'} props.type - Type of date values in column
 * @returns {JSX.Element} Scrollable date picker column
 */
export default function DatePickerMobileSelectorColumn<T>({
  content,
  onSelectedItem,
  currentIndex: current,
  type,
}: DatePickerMobileSelectorColumnProps<T>) {
  // Height of each item in pixels
  const itemHeight = 40;

  // Get current language for date formatting
  const { lang } = useTranslation();
  const formatMapping = dateFormatMapping.get(
    (lang as Language) ?? Language.English,
  );

  // Get scroll handling functions and state from custom hook
  const {
    containerRef,
    getItemStyle,
    currentIndex,
    onMouseDown,
    onTouchStart,
  } = useScrollEffect({
    itemHeight,
    content,
    callback: onSelectedItem,
    current,
  });

  // Memoized list of date cells to prevent unnecessary re-renders
  const Cells = useMemo(
    () =>
      content?.map((value, index) => (
        <DatePickerMobileSelectorColumnCell
          value={value}
          key={`date-picker-cell-${value}`}
          index={index}
          type={type}
          style={getItemStyle(index)}
          isCurrent={index == currentIndex}
        />
      )),
    [content, getItemStyle, currentIndex, type],
  );

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${styles[`container--order-${formatMapping?.[type]}`]}`}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      {Cells}
    </div>
  );
}
